/**
 * ResourcePlanner - Drag & Drop Scheduling Calendar
 *
 * Resource timeline view for assigning workers to worksites.
 * Y-axis: Employees (grouped by category)
 * X-axis: Days of the week
 *
 * Features:
 * - Drag worksites from sidebar to schedule
 * - Visual indicators for overtime, vacations, document status
 * - Week navigation
 * - Bulk operations
 *
 * Orchestrator: owns state + data-fetching and composes the sub-components in
 * ./resource-planner/ (WeekNavigation, WorksiteSidebar, CalendarCell, ShiftModal).
 */
import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from './AdminLayout';
import {
  getWeekCalendar,
  getEmployeesForPlanning,
  getWorksitesForPlanning,
  createShift,
  deleteShift,
  copyWeek,
  Employee,
  WorksiteForPlanning,
  PlannedShift,
  WeekCalendarResponse,
} from '../../services/apiClient';
import { useAppStore } from '../../store/useAppStore';
import { WeekNavigation } from './resource-planner/WeekNavigation';
import { WorksiteSidebar } from './resource-planner/WorksiteSidebar';
import { CalendarGrid } from './resource-planner/CalendarGrid';
import { ShiftModal } from './resource-planner/ShiftModal';

export function ResourcePlanner() {
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    monday.setHours(0, 0, 0, 0);
    return monday;
  });

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [worksites, setWorksites] = useState<WorksiteForPlanning[]>([]);
  const [calendar, setCalendar] = useState<WeekCalendarResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showToast } = useAppStore();

  // Drag state
  const [draggedWorksite, setDraggedWorksite] = useState<WorksiteForPlanning | null>(null);
  const [dragOverCell, setDragOverCell] = useState<{ empId: number; date: string } | null>(null);

  // Modal state
  const [modalData, setModalData] = useState<{
    isOpen: boolean;
    employeeId: number;
    employeeName: string;
    date: string;
  }>({ isOpen: false, employeeId: 0, employeeName: '', date: '' });

  // `weekStart` es lunes 00:00 LOCAL del navegador. `toISOString()` lo pasa a
  // UTC y en cualquier zona por delante de Greenwich (Europe/Madrid) el lunes
  // 00:00 local es el DOMINGO 22:00/23:00 UTC: se pedía `week_start=domingo`
  // y la rejilla era domingo–sábado mientras la barra decía lunes–domingo
  // (ronda 3, r2b21[5]). Se serializa con los componentes LOCALES — el mismo
  // cuidado que `sumarDias` (tanda 4): nunca toISOString para una fecha de
  // calendario.
  const weekStartStr = [
    weekStart.getFullYear(),
    String(weekStart.getMonth() + 1).padStart(2, '0'),
    String(weekStart.getDate()).padStart(2, '0'),
  ].join('-');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [emps, wss, cal] = await Promise.all([
        getEmployeesForPlanning(weekStartStr),
        getWorksitesForPlanning(),
        getWeekCalendar(weekStartStr),
      ]);
      // Las horas asignadas de la semana se calculan del PROPIO calendario
      // (suma de los turnos de cada fila): antes se rellenaban con las horas
      // de CONTRATO del trabajador y el badge «⚠️ HE» saltaba en toda jornada
      // completa aunque el cuadrante estuviera vacío (ronda 3, r2b21[8]).
      const horasAsignadas = new Map<number, number>();
      // Asignados por centro: personas distintas con turno en ese centro
      // durante la semana visible. Es el dato que la tarjeta del sidebar
      // prometía con `assigned_workers`, un campo que `GET /worksites` no
      // trae y se pintaba «undefined/undefined asignados» (ronda 3,
      // r2b21[10]).
      const asignadosPorCentro = new Map<number, number>();
      for (const fila of cal.users) {
        let horas = 0;
        const centros = new Set<number>();
        for (const celda of Object.values(fila.days)) {
          horas += celda?.expected_hours ?? 0;
          if (celda) centros.add(celda.worksite_id);
        }
        horasAsignadas.set(fila.user_id, horas);
        for (const wsId of centros) {
          asignadosPorCentro.set(wsId, (asignadosPorCentro.get(wsId) ?? 0) + 1);
        }
      }
      setEmployees(
        emps.map((e) => ({ ...e, weekly_hours_assigned: horasAsignadas.get(e.id) ?? 0 })),
      );
      setWorksites(
        wss.map((w) => ({ ...w, assigned_workers: asignadosPorCentro.get(w.id) ?? 0 })),
      );
      setCalendar(cal);
    } catch (error) {
      console.error('Failed to load planner data:', error);
      showToast('Error al cargar datos', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [weekStartStr, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get shifts for a specific employee and date
  // El backend manda `user_id` (no `id`) y UNA celda por usuario y día (objeto,
  // no lista): el contrato se consume aquí, en el único sitio que lo usa, y la
  // celda se adapta a la forma que pinta CalendarCell. Con `u.id` y array el
  // cuadrante salía vacío siempre (ronda 3, r2b06/r2b21).
  const getShiftsForCell = (employeeId: number, date: string): PlannedShift[] => {
    if (!calendar) return [];
    const fila = calendar.users.find((u) => u.user_id === employeeId);
    const celda = fila?.days?.[date];
    if (!celda) return [];
    return [
      {
        id: celda.shift_id,
        user_id: employeeId,
        user_name: fila?.user_name ?? null,
        worksite_id: celda.worksite_id,
        worksite_name: celda.worksite_name,
        worksite_code: celda.worksite_code,
        date,
        expected_start_time: celda.expected_start_time,
        expected_end_time: celda.expected_end_time,
        expected_hours: celda.expected_hours,
        is_confirmed: false,
        notes: celda.notes,
      },
    ];
  };

  // Handle drop
  const handleDrop = (employeeId: number, date: string) => {
    if (!draggedWorksite) return;

    const employee = employees.find((e) => e.id === employeeId);
    if (!employee) return;

    setModalData({
      isOpen: true,
      employeeId,
      employeeName: `${employee.first_name} ${employee.last_name}`,
      date,
    });
    setDragOverCell(null);
  };

  // Confirm shift creation
  const handleConfirmShift = async (startTime: string, endTime: string) => {
    if (!draggedWorksite) return;

    try {
      await createShift({
        user_id: modalData.employeeId,
        worksite_id: draggedWorksite.id,
        date: modalData.date,
        expected_start_time: startTime,
        expected_end_time: endTime,
      });
      showToast('Turno asignado', 'success');
      loadData();
    } catch (error) {
      console.error('Failed to create shift:', error);
      showToast('Error al asignar turno', 'error');
    } finally {
      setModalData({ ...modalData, isOpen: false });
      setDraggedWorksite(null);
    }
  };

  // Delete shift
  const handleDeleteShift = async (shiftId: number) => {
    if (!confirm('¿Eliminar este turno?')) return;

    try {
      await deleteShift(shiftId);
      showToast('Turno eliminado', 'success');
      loadData();
    } catch (error) {
      console.error('Failed to delete shift:', error);
      showToast('Error al eliminar turno', 'error');
    }
  };

  // Copiar la semana ANTERIOR sobre la visible (ronda 3, r2b21[9]): el
  // endpoint copy-week existía desde siempre y el botón no llamaba a nada.
  // create_planned_shift sobrescribe el turno existente de cada usuario y
  // día, así que se avisa de que la copia sustituye lo que hubiera.
  const [copying, setCopying] = useState(false);

  const handleCopyWeek = async () => {
    const previa = new Date(weekStart);
    previa.setDate(previa.getDate() - 7);
    const previaStr = [
      previa.getFullYear(),
      String(previa.getMonth() + 1).padStart(2, '0'),
      String(previa.getDate()).padStart(2, '0'),
    ].join('-');

    const ok = confirm(
      `¿Copiar los turnos de la semana del ${previaStr} a la del ${weekStartStr}?\n` +
      'La copia SUSTITUYE el turno que ya tenga cada trabajador esos días.',
    );
    if (!ok) return;

    setCopying(true);
    try {
      const creados = await copyWeek({
        source_week_start: previaStr,
        target_week_start: weekStartStr,
      });
      showToast(`Semana copiada (${creados.length} turno/s)`, 'success');
      loadData();
    } catch (error) {
      console.error('Failed to copy week:', error);
      showToast('Error al copiar la semana', 'error');
    } finally {
      setCopying(false);
    }
  };

  // Generate week days
  const weekDays = calendar?.days || [];

  // Group employees by category
  const employeesByCategory = employees.reduce(
    (acc, emp) => {
      const category = emp.category || 'Sin categoria';
      if (!acc[category]) acc[category] = [];
      acc[category].push(emp);
      return acc;
    },
    {} as Record<string, Employee[]>
  );

  return (
    <AdminLayout title="Planificacion" subtitle="Torre de control - Asignacion de personal">
      {/* Toolbar */}
      <div className="mb-6 flex items-center justify-between bg-white p-4 rounded-lg border border-line">
        <WeekNavigation weekStart={weekStart} onWeekChange={setWeekStart} />

        <div className="flex gap-2">
          {/* «Guardar plantilla» no tiene endpoint en el backend (no existe
              ninguna plantilla de cuadrante): era un botón visible sin
              handler que no llamaba a nada. Fuera hasta que exista el
              endpoint (ronda 3, r2b21[9]). */}
          <button
            onClick={handleCopyWeek}
            disabled={copying}
            className="px-4 py-2 text-sm bg-surface-2 text-ink-2 rounded-lg hover:bg-surface-3 transition-colors disabled:opacity-50"
          >
            {copying ? 'Copiando…' : 'Copiar semana'}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-brand border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="flex gap-6">
          {/* Worksite sidebar */}
          <WorksiteSidebar
            worksites={worksites}
            onDragStart={setDraggedWorksite}
            onDragEnd={() => {
              setDraggedWorksite(null);
              setDragOverCell(null);
            }}
          />

          {/* Calendar grid */}
          <CalendarGrid
            weekDays={weekDays}
            employeesByCategory={employeesByCategory}
            draggedWorksite={draggedWorksite}
            dragOverCell={dragOverCell}
            getShiftsForCell={getShiftsForCell}
            onCellDragEnter={(empId, date) => setDragOverCell({ empId, date })}
            onCellDragLeave={() => setDragOverCell(null)}
            onDrop={handleDrop}
            onDeleteShift={handleDeleteShift}
          />
        </div>
      )}

      {/* Shift creation modal */}
      <ShiftModal
        isOpen={modalData.isOpen}
        employeeName={modalData.employeeName}
        date={modalData.date}
        worksite={draggedWorksite}
        onConfirm={handleConfirmShift}
        onCancel={() => {
          setModalData({ ...modalData, isOpen: false });
          setDraggedWorksite(null);
        }}
      />

      {/* Add CSS for striped vacation cells */}
      <style>{`
        .bg-stripes {
          background-image: repeating-linear-gradient(
            45deg,
            transparent,
            transparent 5px,
            rgba(0, 0, 0, 0.03) 5px,
            rgba(0, 0, 0, 0.03) 10px
          );
        }
      `}</style>
    </AdminLayout>
  );
}
